/**
 * useDocumentTitle — names the browser tab "{page} · {tenant} · Switchboard".
 *
 * Mounted once, by the admin layout's PageTitleProvider. The page name is the
 * leaf route's trail label, or the title a page set with `useSetPageTitle`.
 * It never restores the previous title on unmount: with lazy-loaded pages a
 * restore can land after the next page has set its own, leaving a stale tab.
 *
 * useAppDocumentTitle, mounted once at the router's root, names the tab on the
 * routes outside the admin layout, so no tenant's name is left on the sign-in
 * page after a session ends.
 */

import { useContext, useEffect } from 'react';
import { useMatches } from '@tanstack/react-router';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { AdminAuthContext } from '../contexts/adminAuthContextDef';
import { pageTrailLabel, tenantLabel } from './usePageTrail';

export const APP_TITLE = 'Switchboard';

export function formatDocumentTitle(page: string | null, tenant: string | null): string {
  return [page, tenant, APP_TITLE].filter(Boolean).join(' · ');
}

export function useDocumentTitle(pageTitle?: string): void {
  const matches = useMatches();
  const { user } = useAdminAuth();
  const leaf = matches[matches.length - 1];
  const page = pageTitle
    ?? (leaf ? pageTrailLabel({ routeId: leaf.routeId, search: leaf.search as Record<string, unknown> }) : null);
  const title = formatDocumentTitle(page, tenantLabel(user));
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/** The admin layout's route id: its pages are named by useDocumentTitle. */
const ADMIN_LAYOUT_ID = '/admin';

/**
 * Names the tab on every route outside the admin layout: "Chat · {tenant} ·
 * Switchboard" on the chat page, and plain "Switchboard" on the public pages
 * (sign-in, password reset, sign-up), which have no tenant to name.
 * Inside the admin layout it does nothing.
 */
export function useAppDocumentTitle(): void {
  const matches = useMatches();
  // Read without throwing: the root also renders where no auth provider is.
  const user = useContext(AdminAuthContext)?.user;
  const inAdmin = matches.some((m) => m.routeId === ADMIN_LAYOUT_ID);
  const leafId = matches[matches.length - 1]?.routeId;
  const title = inAdmin ? null : leafId === '/chat' ? formatDocumentTitle('Chat', tenantLabel(user)) : APP_TITLE;
  useEffect(() => {
    if (title !== null) document.title = title;
  }, [title]);
}
